import React from 'react';
import { Link } from 'react-router-dom';
import {
    FiPhone,
    FiMapPin,
    FiClock,
    FiCheckCircle,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import {
    BUSINESS_ADDRESS_LINES,
    BUSINESS_HOURS,
    BUSINESS_MAP_URL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_PHONE_PTCL,
    BUSINESS_WHATSAPP_CHAT_URL,
    RAHBER_LOGO_URL,
    RAHBER_LOGO_DARK_URL,
    SOCIAL_DEALS_BLURB,
} from '../../config/businessInfo';
import { useTheme } from '../../context/ThemeContext';
import './Footer.css';

// Real Official Colorful Social Media Icons (Provided by User)
const FOOTER_SOCIAL_LINKS = [
    {
        id: 'youtube',
        label: 'YouTube',
        href: 'https://www.youtube.com/@rahbercorporation',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056670/youtube_1_rwxjcu.png',
    },
    {
        id: 'tiktok',
        label: 'TikTok',
        href: 'https://www.tiktok.com/@rahbercorporation',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/tiktok_nfltaf.png',
    },
    {
        id: 'instagram',
        label: 'Instagram',
        href: 'https://www.instagram.com/rahber_corporation/',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/Insta_cwhaoa.png',
    },
    {
        id: 'facebook',
        label: 'Facebook',
        href: 'https://www.facebook.com/share/1GpwPPR4Bp/',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/Fb_ermoa9.png',
    },
];

// Official Services / Payment Badges (Provided by User)
const FOOTER_PAYMENT_SERVICES = [
    {
        id: 'installment',
        title: 'Installments',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791059606/installment_dr0771.png',
    },
    {
        id: 'bank',
        title: 'Bank Transfer',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791059366/bank-svgrepo-com_jk1bam.svg',
    },
    {
        id: 'delivery',
        title: 'Doorstep Delivery',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791059366/delivery-truck_nz3uyy.png',
    },
];

function SocialIcon({ link }) {
    return (
        <img
            src={link.iconUrl}
            alt={link.label}
            className="footer-social-img"
            loading="lazy"
        />
    );
}

const Footer = () => {
    const { isDark } = useTheme();

    const handleLogoClick = (e) => {
        if (window.location.pathname === '/') {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <footer className="footer" aria-label="Site footer">
            {/* Top trust highlights */}
            <div className="footer-highlights">
                <div className="container">
                    <div className="footer-highlights-grid">
                        <div className="footer-highlight-item">
                            <FiCheckCircle className="highlight-icon" aria-hidden="true" />
                            <div>
                                <span className="highlight-title">Original Factory Sealed</span>
                                <span className="highlight-subtitle">Official brand warranty card included</span>
                            </div>
                        </div>
                        <div className="footer-highlight-item">
                            <FiCheckCircle className="highlight-icon" aria-hidden="true" />
                            <div>
                                <span className="highlight-title">Flexible Installments</span>
                                <span className="highlight-subtitle">Transparent monthly plans up to 18 months</span>
                            </div>
                        </div>
                        <div className="footer-highlight-item">
                            <FiCheckCircle className="highlight-icon" aria-hidden="true" />
                            <div>
                                <span className="highlight-title">Direct Showroom Support</span>
                                <span className="highlight-subtitle">Lahore showroom open Monday to Saturday</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="footer-top">
                <div className="container">
                    <div className="footer-grid">
                        {/* 01: Brand Column */}
                        <div className="footer-col footer-col-brand">
                            <Link to="/" onClick={handleLogoClick} className="footer-logo-link" aria-label="Rahber Corporation Home">
                                <img
                                    src={isDark ? RAHBER_LOGO_DARK_URL : RAHBER_LOGO_URL}
                                    alt="Rahber Corporation"
                                    className="footer-logo-img"
                                    onError={(e) => {
                                        if (isDark && e.currentTarget.src !== RAHBER_LOGO_URL) {
                                            e.currentTarget.src = RAHBER_LOGO_URL;
                                            return;
                                        }
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.parentElement?.querySelector('.footer-logo-fallback');
                                        if (fallback) fallback.style.display = 'block';
                                    }}
                                />
                                <div className="footer-logo-fallback" style={{ display: 'none' }}>
                                    <span className="footer-logo-brand-text">RAHBER CORPORATION</span>
                                </div>
                            </Link>

                            <p className="footer-desc">
                                Trusted home appliances and electronics retailer in Pakistan since 1980.
                                Providing genuine air conditioners, refrigerators, LED TVs, washing machines, and flexible payment plans.
                            </p>

                            <p className="footer-social-deals">{SOCIAL_DEALS_BLURB}</p>

                            <div className="footer-social-links">
                                {FOOTER_SOCIAL_LINKS.map((link) => (
                                    <a
                                        key={link.id}
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`footer-social-btn footer-social-btn--${link.id}`}
                                        aria-label={`${link.label}, Rahber Corporation`}
                                        title={link.label}
                                    >
                                        <SocialIcon link={link} />
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* 02: Quick Links */}
                        <div className="footer-col">
                            <h3 className="footer-col-title">Quick Links</h3>
                            <ul className="footer-links">
                                <li><Link to="/products">Browse All Products</Link></li>
                                <li><Link to="/installment">Buy on Installment</Link></li>
                                <li><Link to="/cash">Buy on Cash</Link></li>
                                <li><Link to="/blogs">Blogs &amp; Guides</Link></li>
                                <li><Link to="/about">About Rahber</Link></li>
                                <li><Link to="/contact">Contact &amp; Showroom</Link></li>
                            </ul>
                        </div>

                        {/* 03: Customer Service */}
                        <div className="footer-col">
                            <h3 className="footer-col-title">Customer Service</h3>
                            <ul className="footer-links">
                                <li><Link to="/faq">Frequently Asked Questions</Link></li>
                                <li><Link to="/shipping">Delivery &amp; Shipping Policy</Link></li>
                                <li><Link to="/returns">Return &amp; Refund Policy</Link></li>
                                <li><Link to="/warranty">Warranty Guidelines</Link></li>
                                <li><Link to="/terms">Terms &amp; Conditions</Link></li>
                                <li><Link to="/privacy">Privacy Policy</Link></li>
                            </ul>
                        </div>

                        {/* 04: Showroom & Direct Contact */}
                        <div className="footer-col footer-col-contact">
                            <h3 className="footer-col-title">Showroom &amp; Helplines</h3>
                            <ul className="footer-contact-list">
                                <li className="footer-contact-item">
                                    <FiMapPin className="footer-contact-icon" aria-hidden="true" />
                                    <div>
                                        <strong>Lahore Showroom:</strong>
                                        <div className="footer-address-lines">
                                            {BUSINESS_ADDRESS_LINES.map((line) => (
                                                <div key={line}>{line}</div>
                                            ))}
                                        </div>
                                        <a
                                            className="footer-map-link"
                                            href={BUSINESS_MAP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View location on Google Maps →
                                        </a>
                                    </div>
                                </li>

                                <li className="footer-contact-item">
                                    <FiPhone className="footer-contact-icon" aria-hidden="true" />
                                    <div>
                                        <strong>Helplines &amp; Inquiries:</strong>
                                        <div className="footer-phone-list">
                                            {BUSINESS_PHONE_MAIN.map(({ display, tel }) => (
                                                <a key={tel} href={`tel:${tel}`} className="footer-phone-link">
                                                    {display}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="footer-sub-phone">
                                            PTCL: <a href={`tel:${BUSINESS_PHONE_PTCL.tel}`}>{BUSINESS_PHONE_PTCL.display}</a>
                                        </div>
                                    </div>
                                </li>

                                <li className="footer-contact-item">
                                    <FiClock className="footer-contact-icon" aria-hidden="true" />
                                    <div>
                                        <strong>Working Hours:</strong>
                                        <div className="footer-hours-text">{BUSINESS_HOURS.lines[0]}</div>
                                        <div className="footer-hours-muted">{BUSINESS_HOURS.lines[1]}</div>
                                    </div>
                                </li>
                            </ul>

                            <div className="footer-quick-wa-box">
                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="footer-wa-btn"
                                >
                                    <FaWhatsapp /> Instant WhatsApp Inquiry
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="footer-bottom">
                <div className="container">
                    <div className="footer-bottom-content">
                        <p className="footer-copyright">
                            &copy; {new Date().getFullYear()} Rahber Corporation. All rights reserved.
                        </p>
                        <div className="footer-payment-strip">
                            <span className="footer-payment-label">Payment &amp; Services:</span>
                            <div className="footer-payment-icons">
                                {FOOTER_PAYMENT_SERVICES.map((s) => (
                                    <div key={s.id} className="footer-service-pill" title={s.title}>
                                        <img
                                            src={s.iconUrl}
                                            alt={s.title}
                                            className="footer-service-icon"
                                            loading="lazy"
                                        />
                                        <span className="footer-service-title">{s.title}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
